import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamPlatform, ParamProjectSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { logIssue } from "./logging-B7X3yL0T.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { structuredResult } from "./results-NF1RJbDZ.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/update-project.ts
const updateProjectOutputSchema = z.object({ project: z.object({
	id: z.string(),
	slug: z.string(),
	name: z.string(),
	platform: z.string().nullable()
}) });
var update_project_default = defineTool({
	name: "update_project",
	skills: ["project-management"],
	requiredScopes: ["project:write"],
	description: [
		"Update project metadata in Sentry, such as name, slug, and platform.",
		"",
		"Be careful when using this tool!",
		"",
		"Use this tool when you need to:",
		"- Update a project's name or slug to fix onboarding mistakes",
		"- Change the platform assigned to a project",
		"",
		"<examples>",
		"### Update a project's name and slug",
		"",
		"```",
		"update_project(organizationSlug='my-organization', projectSlug='old-project', name='New Project Name', slug='new-project-slug')",
		"```",
		"",
		"### Update platform",
		"",
		"```",
		"update_project(organizationSlug='my-organization', projectSlug='my-project', platform='python')",
		"```",
		"",
		"</examples>",
		"",
		"<hints>",
		"- If the user passes a parameter in the form of name/otherName, it's likely in the format of <organizationSlug>/<projectSlug>.",
		"- Team access changes are handled by separate project-management tools.",
		"- If any parameter is ambiguous, you should clarify with the user what they meant.",
		"- When updating the slug, the project will be accessible at the new slug after the update",
		"- Do not update the slug from a project-scoped session; reconnect with an organization-scoped or unconstrained session first.",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		projectSlug: ParamProjectSlug,
		name: z.string().trim().describe("The new name for the project").nullable().default(null),
		slug: ParamProjectSlug.describe("The new slug for the project (must be unique)").nullable().default(null),
		platform: ParamPlatform.nullable().default(null)
	},
	annotations: {
		readOnlyHint: false,
		destructiveHint: true,
		idempotentHint: false,
		openWorldHint: true
	},
	outputSchema: updateProjectOutputSchema,
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		setTag("project.slug", params.projectSlug);
		const hasProjectUpdates = params.name || params.slug || params.platform;
		if (!hasProjectUpdates) throw new UserInputError("At least one project metadata field is required: `name`, `slug`, or `platform`.");
		if (params.slug && context.constraints.projectSlug) throw new UserInputError("Project slug changes require an organization-scoped or unconstrained session. Reconnect without a project constraint before renaming the project slug.");
		let project;
		try {
			project = await apiService.updateProject({
				organizationSlug,
				projectSlug: params.projectSlug,
				name: params.name,
				slug: params.slug,
				platform: params.platform
			});
		} catch (err) {
			logIssue(err);
			throw new Error(`Failed to update project ${params.projectSlug}: ${err instanceof Error ? err.message : "Unknown error"}`, { cause: err });
		}
		return structuredResult({ project: {
			id: String(project.id),
			slug: project.slug,
			name: project.name,
			platform: project.platform ?? null
		} });
	}
});

//#endregion
export { updateProjectOutputSchema, update_project_default };
//# sourceMappingURL=update-project-BYY10KTt.js.map